/**
 * Supabase Company Service
 * Handles company data operations with Supabase backend
 */

import { supabase } from './client';

export interface SupabaseCompany {
  id: string;
  legacy_id: string;
  company_name: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo_url?: string;
  is_active: boolean;
  sync_source?: string;
  synced_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyFilters {
  search?: string;
  is_active?: boolean;
  legacy_id?: string;
}

export interface CompanyStats {
  totalCompanies: number;
  activeCompanies: number;
  inactiveCompanies: number;
  companiesWithUsers: number;
  companiesWithTeams: number;
  companiesWithReportGroups: number;
}

class CompanyService {
  /**
   * Get all companies
   */
  async getCompanies(filters: CompanyFilters = {}): Promise<SupabaseCompany[]> {
    try {
      let query = supabase
        .from('companies')
        .select('*')
        .order('company_name', { ascending: true });

      // Apply filters
      if (filters.search) {
        query = query.or(`company_name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      if (filters.is_active !== undefined) {
        query = query.eq('is_active', filters.is_active);
      }

      if (filters.legacy_id) {
        query = query.eq('legacy_id', filters.legacy_id);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  }

  /**
   * Get a single company by ID
   */
  async getCompanyById(id: string): Promise<SupabaseCompany | null> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching company:', error);
      throw error;
    }
  }

  /**
   * Get a company by legacy ID (CompID from SQL Server)
   */
  async getCompanyByLegacyId(legacyId: string): Promise<SupabaseCompany | null> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('legacy_id', legacyId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching company by legacy ID:', error);
      throw error;
    }
  }

  /**
   * Create a new company
   */
  async createCompany(company: Omit<SupabaseCompany, 'id' | 'created_at' | 'updated_at'>): Promise<SupabaseCompany> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .insert([{
          ...company,
          synced_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  }

  /**
   * Update a company
   */
  async updateCompany(id: string, updates: Partial<SupabaseCompany>): Promise<SupabaseCompany> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  }

  /**
   * Delete a company (soft delete by setting is_active = false)
   */
  async deleteCompany(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  }

  /**
   * Get company statistics
   */
  async getCompanyStats(): Promise<CompanyStats> {
    try {
      // Get company counts
      const { data: companies, error: companiesError } = await supabase
        .from('companies')
        .select('id, is_active');

      if (companiesError) {
        throw companiesError;
      }

      const totalCompanies = companies?.length || 0;
      const activeCompanies = companies?.filter(c => c.is_active).length || 0;
      const inactiveCompanies = totalCompanies - activeCompanies;

      // Get companies with users
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('comp_id')
        .not('comp_id', 'is', null);

      const companiesWithUsers = new Set(usersData?.map(u => u.comp_id)).size;

      // Get companies with teams
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select('comp_id')
        .not('comp_id', 'is', null);

      const companiesWithTeams = new Set(teamsData?.map(t => t.comp_id)).size;

      // Get companies with report groups
      const { data: reportGroupsData, error: reportGroupsError } = await supabase
        .from('report_groups')
        .select('comp_id')
        .not('comp_id', 'is', null);

      const companiesWithReportGroups = new Set(reportGroupsData?.map(rg => rg.comp_id)).size;

      return {
        totalCompanies,
        activeCompanies,
        inactiveCompanies,
        companiesWithUsers: usersError ? 0 : companiesWithUsers,
        companiesWithTeams: teamsError ? 0 : companiesWithTeams,
        companiesWithReportGroups: reportGroupsError ? 0 : companiesWithReportGroups,
      };
    } catch (error) {
      console.error('Error fetching company stats:', error);
      return {
        totalCompanies: 0,
        activeCompanies: 0,
        inactiveCompanies: 0,
        companiesWithUsers: 0,
        companiesWithTeams: 0,
        companiesWithReportGroups: 0,
      };
    }
  }

  /**
   * Get active companies for dropdown/select components
   */
  async getActiveCompanies(): Promise<SupabaseCompany[]> {
    return this.getCompanies({ is_active: true });
  }
}

export const companyService = new CompanyService();